import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Row, Column, Button, Alert, TextInput, TextArea, Select, Icon } from '@pella/sash-framework';
import { isValidArray, isValidEmail, isValidPhoneNumber, isValidPostal } from '../../../../utilities/validations';
import { useRecaptcha } from '../../../../../hooks/use-recaptcha';
import { useMutation } from '@apollo/client';
import { SUBMIT_PCC_QUOTE_FORM } from '../../../../../enums/graphql';

import EloquaForm from '../../../../common/eloqua-form';
import Recaptcha from '../../recaptcha';

const PccQuoteForm = (props) => {
	const [values, setValues] = useState({
		firstName: '',
		lastName: '',
		address1: '',
		address2: '',
		city: '',
		state: '',
		postalCode: '',
		phone: '',
		userEmailAddress: '',
		projectComments: '',
		form: {},
	});
	const [errors, setErrors] = useState([]);
	const [status, setStatus] = useState('idle');
	const { captchaToken, setCaptchaToken, verifyRecaptchaToken } = useRecaptcha();
	const [submitForm] = useMutation(SUBMIT_PCC_QUOTE_FORM, {
		onCompleted: () => props.onSubmit(3),
		onError: (error) => {
			setStatus('error');
			setErrors({ form: error.message });
		},
	});

	const validateForm = () => {
		let errors = {};

		if (!values.firstName) {
			errors.firstName = 'First name is required';
		}

		if (!values.lastName) {
			errors.lastName = 'Last name is required';
		}

		if (!values.address1) {
			errors.address1 = 'Address 1 is required';
		}

		if (!values.city) {
			errors.city = 'City is required';
		}

		if (!values.state) {
			errors.state = 'State is required';
		}

		if (!isValidPostal(values.postalCode)) {
			errors.postalCode = 'Zip code is required';
		}

		if (!isValidPhoneNumber(values.phone)) {
			errors.phone = 'Preferred phone number is required';
		}

		if (!isValidEmail(values.userEmailAddress)) {
			errors.userEmailAddress = 'Email address is required';
		}

		if (!captchaToken) {
			errors.recaptcha = 'You must complete the Captcha';
		}

		setErrors(errors);

		return Object.keys(errors).length === 0;
	};

	const onEmailBlur = (e) => {
		let errors = [];

		if (!isValidEmail(e.target.value)) {
			errors.userEmailAddress = 'Please enter a valid email address';
		}

		setErrors(errors);
	};

	const onFormSubmit = async (e) => {
		e.preventDefault();
		setStatus('loading');

		if (!validateForm()) {
			setStatus('idle');
			return;
		}

		const formElement = e.target;
		const hiddenFields = Array.from(formElement.elements)
			.filter((el) => el.type === 'hidden')
			.reduce((acc, el) => {
				acc[el.name] = el.value;
				return acc;
			}, {});

		const contractors = props.ppccEliteData.concat(props.ppccData, props.pccData).slice(0, 3);

		const utmFields = ['utmCampaign', 'utmSource', 'utmMedium', 'utmContent', 'utmTerm'];
		const utmData = utmFields.reduce((acc, field) => {
			if (hiddenFields[field]) {
				acc[field] = hiddenFields[field];
			}
			return acc;
		}, {});

		const input = {
			data: {
				elqFormName: hiddenFields.elqFormName,
				elqSiteID: hiddenFields.elqSiteID,
				elqCustomerGUID: hiddenFields.elqCustomerGUID,
				elqCookieWrite: parseInt(hiddenFields.elqCookieWrite || '1', 10),
				env: hiddenFields.env,
				recaptchaResponse: captchaToken,
				firstName: values.firstName,
				lastName: values.lastName,
				address1: values.address1,
				address2: values.address2 || null,
				city: values.city,
				state: values.state,
				postalCode: values.postalCode,
				phone: values.phone,
				userEmailAddress: values.userEmailAddress,
				projectComments: values.projectComments || null,
				contractorName1: contractors[0]?.name || '',
				contractorAddress1_1: contractors[0]?.line_1 || '',
				contractorAddress2_1: contractors[0]?.line_2 || null,
				contractorCity1: contractors[0]?.city || '',
				contractorState1: contractors[0]?.state || '',
				contractorZip1: contractors[0]?.postal_code || '',
				contractorPhone1: contractors[0]?.phone || '',
				contractorEmailAddress1: contractors[0]?.email || '',
				contractorPCCLevel1: contractors[0]?.level || null,
				contractorBranchID1: contractors[0]?.branch_id || null,
				contractorName2: contractors[1]?.name || null,
				contractorAddress1_2: contractors[1]?.line_1 || null,
				contractorAddress2_2: contractors[1]?.line_2 || null,
				contractorCity2: contractors[1]?.city || null,
				contractorState2: contractors[1]?.state || null,
				contractorZip2: contractors[1]?.postal_code || null,
				contractorPhone2: contractors[1]?.phone || null,
				contractorEmailAddress2: contractors[1]?.email || null,
				contractorPCCLevel2: contractors[1]?.level || null,
				contractorBranchID2: contractors[1]?.branch_id || null,
				contractorName3: contractors[2]?.name || null,
				contractorAddress1_3: contractors[2]?.line_1 || null,
				contractorAddress2_3: contractors[2]?.line_2 || null,
				contractorCity3: contractors[2]?.city || null,
				contractorState3: contractors[2]?.state || null,
				contractorZip3: contractors[2]?.postal_code || null,
				contractorPhone3: contractors[2]?.phone || null,
				contractorEmailAddress3: contractors[2]?.email || null,
				contractorPCCLevel3: contractors[2]?.level || null,
				contractorBranchID3: contractors[2]?.branch_id || null,
				...utmData,
			},
		};

		try {
			await submitForm({ variables: { input } });
			setStatus('idle');
		} catch {
			setStatus('idle');
		}
	};

	const onInputChange = (e) => {
		setValues({
			...values,
			[e.target.name]: e.target.value.trim(),
		});
	};

	const onNumberInputChange = (name, value) => {
		setValues({
			...values,
			[name]: value,
		});
	};

	const renderPccBlock = (pcc) => {
		const img = pcc.level === 'Platinum Elite' ? 'https://images.contentstack.io/v3/assets/bltf589e66bcaecd79c/bltbcabe4d1983315f2/5e4f3ab6aa10350d40de74ca/platinum-elite-contractor.png' : pcc.level === 'Platinum' ? 'https://images.contentstack.io/v3/assets/bltf589e66bcaecd79c/blt27f3827463423bdb/5e4f3ab67569ef0d9eca4e13/platinum-contractor_(1).png' : pcc.level === 'Platinum Premier' ? 'https://images.contentstack.io/v3/assets/bltf589e66bcaecd79c/blt52f2586488cbec33/pcc-premier.svg?width=40' : 'https://images.contentstack.io/v3/assets/bltf589e66bcaecd79c/bltbfcb142ab4adc43a/5e4f3ab618f75a0d4c868bee/pella-certified-contractor-logo_0.png';

		return (
			<Row
				key={pcc.id}
				className='mt-10 text-center md:text-left'
				verticalGutters={true}
			>
				<Column
					xs={12}
					sm='auto'
				>
					<img
						src={img}
						alt={pcc.level}
					/>
				</Column>

				<Column
					xs={12}
					sm='fluid'
				>
					<h4 className='mb-1'>{pcc.name}</h4>
					<p>
						{pcc.line_1}
						{pcc.line_2 && <span className='block'>{pcc.line_2}</span>}
						<span className='hidden md:block'>{pcc.phone}</span>
						<span className='md:hidden'>{pcc.phone}</span>
					</p>
				</Column>
			</Row>
		);
	};

	const pcc1 = props.ppccEliteData;
	const pcc2 = props.ppccData;
	const pcc3 = props.pccData;

	const pccResult = pcc1.concat(pcc2, pcc3);
	var counter = 0;

	return (
		<div
			id='pcc-request-quote-form'
			data-testid='pcc-quote-form'
		>
			<h2>Request Project Quotes</h2>
			<p className='max-w-[80ch]'>Please provide the following contact information, so that selected Pella Certified Contractors (shown below) can follow up with you to learn more about your project.</p>

			{Object.keys(errors).length > 0 && (
				<Alert
					type='error'
					className='my-4'
					hideClose
				>
					Please fix any errors in the form and try submitting again.
				</Alert>
			)}

			<Row
				className='mt-10'
				largeGutters
			>
				<Column
					xs={12}
					md={6}
				>
					<EloquaForm
						elqFormName='PccForm-6bdf489d-94d5-40d6-81af-b7af2305d559'
						onFormSubmit={onFormSubmit}
					>
						<Row>
							<Column
								xs={12}
								md={6}
							>
								<TextInput
									id='firstName'
									labelText='First Name'
									onChange={onInputChange}
									required={true}
									errorText={errors.firstName}
									error={errors.firstName}
									autoComplete='given-name'
								/>
							</Column>

							<Column
								xs={12}
								md={6}
							>
								<TextInput
									id='lastName'
									labelText='Last Name'
									onChange={onInputChange}
									required={true}
									errorText={errors.lastName}
									error={errors.lastName}
									autoComplete='family-name'
								/>
							</Column>

							<Column xs={12}>
								<TextInput
									id='address1'
									labelText='Address 1'
									onChange={onInputChange}
									required={true}
									errorText={errors.address1}
									error={errors.address1}
									autoComplete='on'
								/>
							</Column>

							<Column xs={12}>
								<TextInput
									id='address2'
									labelText='Address 2 (optional)'
									onChange={onInputChange}
									autoComplete='on'
								/>
							</Column>

							<Column xs={12}>
								<TextInput
									id='city'
									labelText='City'
									onChange={onInputChange}
									required={true}
									errorText={errors.city}
									error={errors.city}
									autoComplete='on'
								/>
							</Column>

							<Column
								xs={12}
								md={6}
							>
								<Select
									id='state'
									labelText='State'
									onChange={onInputChange}
									required={true}
									errorText={errors.state}
									error={errors.state}
									autoComplete='on'
								>
									<option value='AL'>Alabama</option>
									<option value='AK'>Alaska</option>
									<option value='AZ'>Arizona</option>
									<option value='AR'>Arkansas</option>
									<option value='CA'>California</option>
									<option value='CO'>Colorado</option>
									<option value='CT'>Connecticut</option>
									<option value='DE'>Delaware</option>
									<option value='FL'>Florida</option>
									<option value='GA'>Georgia</option>
									<option value='HI'>Hawaii</option>
									<option value='ID'>Idaho</option>
									<option value='IL'>Illinois</option>
									<option value='IN'>Indiana</option>
									<option value='IA'>Iowa</option>
									<option value='KS'>Kansas</option>
									<option value='KY'>Kentucky</option>
									<option value='LA'>Louisiana</option>
									<option value='ME'>Maine</option>
									<option value='MD'>Maryland</option>
									<option value='MA'>Massachusetts</option>
									<option value='MI'>Michigan</option>
									<option value='MN'>Minnesota</option>
									<option value='MS'>Mississippi</option>
									<option value='MO'>Missouri</option>
									<option value='MT'>Montana</option>
									<option value='NE'>Nebraska</option>
									<option value='NV'>Nevada</option>
									<option value='NH'>New Hampshire</option>
									<option value='NJ'>New Jersey</option>
									<option value='NM'>New Mexico</option>
									<option value='NY'>New York</option>
									<option value='NC'>North Carolina</option>
									<option value='ND'>North Dakota</option>
									<option value='OH'>Ohio</option>
									<option value='OK'>Oklahoma</option>
									<option value='OR'>Oregon</option>
									<option value='PA'>Pennsylvania</option>
									<option value='RI'>Rhode Island</option>
									<option value='SC'>South Carolina</option>
									<option value='SD'>South Dakota</option>
									<option value='TN'>Tennessee</option>
									<option value='TX'>Texas</option>
									<option value='UT'>Utah</option>
									<option value='VT'>Vermont</option>
									<option value='VA'>Virginia</option>
									<option value='WA'>Washington</option>
									<option value='WV'>West Virginia</option>
									<option value='WI'>Wisconsin</option>
									<option value='WY'>Wyoming</option>
								</Select>
							</Column>

							<Column
								xs={12}
								md={6}
							>
								<TextInput
									id='postalCode'
									labelText='Zip Code'
									onChange={onInputChange}
									required={true}
									errorText={errors.postalCode}
									error={errors.postalCode}
									autoComplete='postal-code'
								/>
							</Column>

							<Column xs={12}>
								<TextInput
									type='tel'
									id='phone'
									labelText='Preferred phone number'
									onValueChange={(values) => onNumberInputChange('phone', values.formattedValue)}
									required={true}
									errorText={errors.phone}
									error={errors.phone}
									format='(###) ###-####'
									allowEmptyFormatting
									autoComplete='on'
								/>
							</Column>

							<Column xs={12}>
								<TextInput
									type='email'
									id='userEmailAddress'
									labelText='Email address'
									onChange={onInputChange}
									onBlur={onEmailBlur}
									required={true}
									errorText={errors.userEmailAddress}
									error={errors.userEmailAddress}
									autoComplete='email'
								/>
							</Column>

							<Column xs={12}>
								<TextArea
									id='projectComments'
									labelText='Project Comments (optional)'
									onChange={onInputChange}
									autoComplete='off'
								/>
							</Column>

							<Column
								xs={12}
								className='mb-2.5'
							>
								<Recaptcha
									token={[captchaToken, setCaptchaToken]}
									source='PCCQ'
								/>
								{errors.recaptcha && (
									<p className='text-red mt-0.5 flex items-center gap-1 text-xs'>
										<Icon
											name='warning'
											size={16}
										/>
										{errors.recaptcha}
									</p>
								)}
							</Column>

							<Column xs={12}>
								<Button
									type='submit'
									color='primary'
									disabled={status === 'loading'}
								>
									Submit Quote Request
								</Button>

								<p className='mt-7 text-base'>Your information is important to us. The information you submit here is intended only for the Pella Certified Contractors you selected and will be used in accordance with our privacy policy.</p>
							</Column>
						</Row>

						{pccResult.map((pcc, index) => {
							counter++;
							return (
								<React.Fragment key={index}>
									<input
										type='hidden'
										id={'contractorName' + counter}
										name={'contractorName' + counter}
										value={pcc.name}
									/>
									<input
										type='hidden'
										id={'contractorAddress1_' + counter}
										name={'contractorAddress1_' + counter}
										value={pcc.line_1}
									/>

									{pcc.line_2 ? (
										<input
											type='hidden'
											id={'contractorAddress2_' + counter}
											name={'contractorAddress2_' + counter}
											value={pcc.line_2}
										/>
									) : (
										<input
											type='hidden'
											id={'contractorAddress2_' + counter}
											name={'contractorAddress2_' + counter}
											value=''
										/>
									)}

									<input
										type='hidden'
										id={'contractorCity' + counter}
										name={'contractorCity' + counter}
										value={pcc.city}
									/>
									<input
										type='hidden'
										id={'contractorState' + counter}
										name={'contractorState' + counter}
										value={pcc.state}
									/>
									<input
										type='hidden'
										id={'contractorZip' + counter}
										name={'contractorZip' + counter}
										value={pcc.postal_code}
									/>
									<input
										type='hidden'
										id={'contractorPhone' + counter}
										name={'contractorPhone' + counter}
										value={pcc.phone}
									/>
									<input
										type='hidden'
										id={'contractorEmailAddress' + counter}
										name={'contractorEmailAddress' + counter}
										value={pcc.email}
									/>
									<input
										type='hidden'
										id={'contractorPCCLevel' + counter}
										name={'contractorPCCLevel' + counter}
										value={pcc.level}
									/>
									<input
										type='hidden'
										id={'contractorBranchID' + counter}
										name={'contractorBranchID' + counter}
										value={pcc.branch_id}
									/>
								</React.Fragment>
							);
						})}
					</EloquaForm>
				</Column>

				<Column
					xs={12}
					md={6}
				>
					<div
						className='bg-neutral-50 p-7 md:p-12'
						id='selectedContractorsContainer'
					>
						<h3>Your quote request will be sent to:</h3>

						{isValidArray(props?.ppccEliteData) && props.ppccEliteData.map((ppccElite) => renderPccBlock(ppccElite))}
						{isValidArray(props?.ppccData) && props.ppccData.map((ppcc) => renderPccBlock(ppcc))}
						{isValidArray(props?.pccData) && props.pccData.map((pcc) => renderPccBlock(pcc))}
					</div>
				</Column>
			</Row>
		</div>
	);
};

PccQuoteForm.propTypes = {
	ppccEliteData: PropTypes.array,
	ppccData: PropTypes.array,
	pccData: PropTypes.array,
	onSubmit: PropTypes.func,
};

export default PccQuoteForm;
